import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  ArrowLeft, Loader2, CheckCircle, XCircle,
  RefreshCw, Link2, Activity, FolderPlus, Database, Replace, Search,
  Wrench, AlertTriangle, ShieldCheck, History
} from "lucide-react";
import { API, authFetch } from "../config/api";
import { errorMessage } from "../utils/errorMessage";
import { LOGO_URL } from "../config/constants";
import EmailDiagnosticTile from "../components/EmailDiagnosticTile";
import ObRejectsTriageTile from "../components/ObRejectsTriageTile";
import BackfillStatusTile from "../components/BackfillStatusTile";
import QboInvoiceSyncTile from "../components/QboInvoiceSyncTile";

const BASE = API.replace(/\/api$/, "");

function getUserEmail() {
  try {
    const userStr = localStorage.getItem("user");
    if (userStr) {
      const u = JSON.parse(userStr);
      if (u.email) return u.email;
    }
  } catch(e) {}
  const email = sessionStorage.getItem("bonsai_user_email") || localStorage.getItem("bonsai_user_email") || "";
  if (email) return email;
  const uid = localStorage.getItem("bonsai_user_id") || "";
  return uid.includes("@") ? uid : "vanessa@bonsai.law";
}

// X-Bonsai-User-Id is left to authFetch, which sends the signed-in user's
// id. Sending the email there made every sealed route answer 401.
function getHeaders() {
  const email = getUserEmail();
  return {
    "X-User-Email": email,
    "Content-Type": "application/json",
  };
}

function ActionButton({ label, icon: Icon, onClick, disabled }) {
  const [state, setState] = useState("idle");
  const [message, setMessage] = useState("");

  const run = async () => {
    setState("loading");
    setMessage("");
    try {
      const result = await onClick();
      setState("success");
      setMessage(typeof result === "string" ? result : JSON.stringify(result).slice(0, 200));
    } catch (err) {
      setState("error");
      setMessage(errorMessage(err, "Request failed"));
    }
  };

  return (
    <div className="flex items-start gap-3">
      <Button
        onClick={run}
        disabled={state === "loading" || disabled}
        className="min-w-[260px] justify-start gap-2 bg-navy-hover border border-navy-border text-txt hover:bg-teal hover:text-white transition-all disabled:opacity-40"
      >
        {state === "loading" ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : (
          <Icon className="w-4 h-4" />
        )}
        {label}
      </Button>
      {state === "success" && (
        <span className="text-sm text-teal flex items-center gap-1 mt-1.5">
          <CheckCircle className="w-4 h-4" /> {message || "Done"}
        </span>
      )}
      {state === "error" && (
        <span className="text-sm text-status-red flex items-center gap-1 mt-1.5">
          <XCircle className="w-4 h-4" /> {message}
        </span>
      )}
    </div>
  );
}

/* ── Per-matter correction row ── */
function MatterRefRow({ matter, onCorrected }) {
  const [newRef, setNewRef] = useState("");
  const [state, setState] = useState("idle"); // idle | loading | success | error
  const [msg, setMsg] = useState("");

  const correct = async () => {
    if (!newRef.trim()) { setMsg("Enter a new reference"); setState("error"); return; }
    setState("loading"); setMsg("");
    try {
      const resp = await authFetch(`${BASE}/api/admin/correct-matter-reference`, {
        method: "POST",
        headers: getHeaders(),
        body: JSON.stringify({
          old_reference: matter.reference,
          new_reference: newRef.trim(),
          submission_id: matter.submission_id || undefined,
          reason: "Manual correction via Admin Tools",
        }),
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.detail || `${resp.status} ${resp.statusText}`);
      }
      const data = await resp.json();
      setState("success");
      setMsg(`Updated ${data.total_updated} doc(s)`);
      onCorrected && onCorrected(matter.submission_id);
    } catch (err) {
      setState("error");
      setMsg(errorMessage(err, "Failed"));
    }
  };

  const done = state === "success";

  return (
    <div className={`flex flex-wrap items-center gap-3 py-3 px-3 rounded-md border transition-all ${done ? "border-teal/40 bg-teal-dim/20 opacity-60" : "border-navy-border bg-navy-card"}`}>
      {/* Matter info */}
      <div className="flex-1 min-w-[200px]">
        <p className="text-sm font-semibold text-txt leading-tight">{matter.client_name || "Unknown client"}</p>
        <p className="text-xs text-txt-muted mt-0.5">
          {matter.matter_type && <span className="mr-2">{matter.matter_type}</span>}
          {matter.fee_earner && <span className="mr-2">· {matter.fee_earner}</span>}
          {matter.opened_date && <span>· opened {matter.opened_date}</span>}
        </p>
        <p className="text-xs font-mono text-status-amber mt-1">{matter.reference}</p>
      </div>

      {/* New ref input */}
      <Input
        placeholder="New reference"
        value={newRef}
        onChange={(e) => setNewRef(e.target.value)}
        disabled={done}
        className="w-44 bg-navy border-navy-border text-txt placeholder:text-txt-faint font-mono text-sm"
      />

      {/* Correct button */}
      <Button
        onClick={correct}
        disabled={done || state === "loading" || !newRef.trim()}
        className="gap-1.5 bg-status-amber/80 hover:bg-status-amber border border-amber-600/40 text-white text-sm disabled:opacity-40"
      >
        {state === "loading" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Replace className="w-3.5 h-3.5" />}
        Correct
      </Button>

      {/* Status */}
      {state === "success" && <span className="text-xs text-teal flex items-center gap-1"><CheckCircle className="w-3.5 h-3.5" />{msg}</span>}
      {state === "error" && <span className="text-xs text-status-red flex items-center gap-1"><XCircle className="w-3.5 h-3.5" />{msg}</span>}
    </div>
  );
}

/* ── Reference Fixer section ── */
function ReferenceFixer() {
  const [searchRef, setSearchRef] = useState("");
  const [lookupState, setLookupState] = useState("idle"); // idle | loading | done | error
  const [matters, setMatters] = useState([]);
  const [lookupMsg, setLookupMsg] = useState("");
  const [corrected, setCorrected] = useState(new Set());

  const lookup = async () => {
    if (!searchRef.trim()) return;
    setLookupState("loading");
    setMatters([]);
    setLookupMsg("");
    setCorrected(new Set());
    try {
      const resp = await authFetch(
        `${BASE}/api/admin/lookup-matter-reference?reference=${encodeURIComponent(searchRef.trim())}`,
        { method: "GET", headers: getHeaders() }
      );
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.detail || `${resp.status} ${resp.statusText}`);
      }
      const data = await resp.json();
      // Expect { matters: [{ submission_id, client_name, reference, matter_type, fee_earner, opened_date }] }
      const list = data.matters || data.results || [];
      setMatters(list);
      setLookupState("done");
      setLookupMsg(list.length === 0 ? "No matters found with that reference." : `Found ${list.length} matter${list.length !== 1 ? "s" : ""}`);
    } catch (err) {
      setLookupState("error");
      setLookupMsg(errorMessage(err, "Lookup failed"));
    }
  };

  const markCorrected = (id) => setCorrected(prev => new Set([...prev, id]));

  return (
    <div className="pt-4 mt-4 border-t border-navy-border space-y-4">
      <h3 className="text-xs font-semibold text-txt-muted uppercase tracking-wider flex items-center gap-2">
        <Replace className="w-3.5 h-3.5" /> Matter Reference Correction
      </h3>

      {/* Step 1 — look up */}
      <div className="flex flex-wrap items-center gap-3">
        <Input
          placeholder="Reference to look up (e.g. BL.SG.03.0987)"
          value={searchRef}
          onChange={(e) => setSearchRef(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && lookup()}
          className="w-64 bg-navy-card border-navy-border text-txt placeholder:text-txt-muted font-mono text-sm"
        />
        <Button
          onClick={lookup}
          disabled={lookupState === "loading" || !searchRef.trim()}
          className="gap-1.5 bg-navy-hover border border-navy-border text-txt hover:bg-teal hover:text-white disabled:opacity-40"
        >
          {lookupState === "loading" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
          Look up
        </Button>
        {lookupMsg && (
          <span className={`text-xs flex items-center gap-1 ${lookupState === "error" ? "text-status-red" : matters.length === 0 ? "text-txt-muted" : "text-txt-muted"}`}>
            {lookupState === "error" && <XCircle className="w-3.5 h-3.5" />}
            {lookupMsg}
          </span>
        )}
      </div>

      {/* Step 2 — per-matter rows */}
      {matters.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs text-txt-muted">Enter the correct reference for each matter individually, then click Correct.</p>
          {matters.map((m) => (
            <MatterRefRow
              key={m.submission_id || m.reference + m.client_name}
              matter={m}
              onCorrected={markCorrected}
            />
          ))}
          {corrected.size === matters.length && matters.length > 0 && (
            <p className="text-xs text-teal flex items-center gap-1 pt-1">
              <CheckCircle className="w-3.5 h-3.5" /> All matters corrected.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

/* ── Rebuild Matter (admin disaster-recovery) ── */
function RebuildMatter() {
  const [matterRef, setMatterRef] = useState("");
  const [proposal, setProposal] = useState(null);
  const [state, setState] = useState("idle"); // idle | loading | preview | applying | applied | error
  const [msg, setMsg] = useState("");
  const [recentRuns, setRecentRuns] = useState(null);

  const dryRun = async () => {
    if (!matterRef.trim()) { setMsg("Enter a matter reference"); setState("error"); return; }
    setState("loading"); setMsg(""); setProposal(null);
    try {
      const resp = await authFetch(`${BASE}/api/admin/recovery/rebuild-matter`, {
        method: "POST",
        headers: getHeaders(),
        body: JSON.stringify({ matter_ref: matterRef.trim(), dry_run: true }),
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data?.detail || `${resp.status} ${resp.statusText}`);
      setProposal(data);
      setState("preview");
    } catch (e) {
      setState("error");
      setMsg(errorMessage(e, "Rebuild preview failed"));
    }
  };

  const apply = async () => {
    if (!proposal?.confirm_token) { setMsg("Run preview first"); setState("error"); return; }
    setState("applying"); setMsg("");
    try {
      const resp = await authFetch(`${BASE}/api/admin/recovery/rebuild-matter`, {
        method: "POST",
        headers: getHeaders(),
        body: JSON.stringify({
          matter_ref: matterRef.trim(),
          dry_run: false,
          confirm_token: proposal.confirm_token,
        }),
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data?.detail || `${resp.status} ${resp.statusText}`);
      setProposal(data);
      setState("applied");
    } catch (e) {
      setState("error");
      setMsg(errorMessage(e, "Apply failed"));
    }
  };

  const loadRuns = async () => {
    try {
      const resp = await authFetch(`${BASE}/api/admin/audit/recovery-runs?limit=20`, { headers: getHeaders() });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data?.detail || `${resp.status}`);
      setRecentRuns(data?.runs || []);
    } catch (e) {
      setMsg(errorMessage(e, "Failed to load runs"));
      setState("error");
    }
  };

  const diffRows = proposal?.diff?.changed_fields
    ? Object.entries(proposal.diff.changed_fields)
    : [];
  const docRows = proposal?.primary_docs || [];
  const sp = proposal?.sharepoint || {};

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-3">
        <Input
          placeholder="Matter reference (e.g. BL.SG.03.0987)"
          value={matterRef}
          onChange={(e) => setMatterRef(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && dryRun()}
          className="w-64 bg-navy-card border-navy-border text-txt placeholder:text-txt-muted font-mono text-sm"
        />
        <Button
          onClick={dryRun}
          disabled={state === "loading" || !matterRef.trim()}
          className="gap-1.5 bg-navy-hover border border-navy-border text-txt hover:bg-teal hover:text-white disabled:opacity-40"
        >
          {state === "loading" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wrench className="w-4 h-4" />}
          Preview rebuild (dry-run)
        </Button>
        <Button
          onClick={loadRuns}
          variant="outline"
          className="gap-1.5 border-navy-border text-txt-muted hover:text-teal hover:border-teal"
        >
          <History className="w-4 h-4" /> Recent runs
        </Button>
      </div>

      {state === "error" && msg && (
        <p className="text-sm text-status-red flex items-center gap-1">
          <XCircle className="w-4 h-4" /> {msg}
        </p>
      )}

      {proposal && (
        <div className="rounded-md border border-navy-border bg-navy-card/50 p-4 space-y-3 text-sm">
          <div className="flex items-center gap-2 text-txt-muted">
            <ShieldCheck className="w-4 h-4 text-teal" />
            SharePoint:
            <span className={sp.found ? "text-teal" : "text-status-red"}>
              folder {sp.found ? "found" : "missing"}
            </span>
            <span>·</span>
            <span className={sp.compliance ? "text-teal" : "text-status-red"}>
              compliance {sp.compliance ? "found" : "missing"}
            </span>
            {sp.safe_mode && <span className="text-yellow-400">(safe mode)</span>}
            {sp.error && <span className="text-yellow-400 truncate">— {sp.error}</span>}
          </div>

          <div>
            <p className="text-xs uppercase text-txt-muted mb-1">Primary docs</p>
            <ul className="space-y-1">
              {docRows.map((d, i) => (
                <li key={i} className="flex items-start gap-2 font-mono text-xs">
                  {d.ok ? (
                    <CheckCircle className="w-3.5 h-3.5 text-teal mt-0.5" />
                  ) : (
                    <AlertTriangle className="w-3.5 h-3.5 text-yellow-400 mt-0.5" />
                  )}
                  <span className="text-txt">{d.kind || "unclassified"}</span>
                  <span className="text-txt-muted">{d.filename || "(missing)"}</span>
                  {d.error && <span className="text-yellow-400">— {d.error}</span>}
                </li>
              ))}
            </ul>
          </div>

          <div>
            <p className="text-xs uppercase text-txt-muted mb-1">Proposed changes</p>
            {diffRows.length === 0 ? (
              <p className="text-xs text-txt-muted">No changes — current DB state already matches the docs.</p>
            ) : (
              <table className="text-xs w-full">
                <thead>
                  <tr className="text-txt-muted">
                    <th className="text-left pr-3">Field</th>
                    <th className="text-left pr-3">Current</th>
                    <th className="text-left">Proposed</th>
                  </tr>
                </thead>
                <tbody>
                  {diffRows.map(([k, v]) => (
                    <tr key={k} className="font-mono">
                      <td className="pr-3 text-txt">{k}</td>
                      <td className="pr-3 text-status-red">{String(v.current ?? "—")}</td>
                      <td className="text-teal">{String(v.proposed ?? "—")}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {state === "preview" && diffRows.length > 0 && sp.found && sp.compliance && (
            <div className="flex items-center gap-3 pt-2 border-t border-navy-border">
              <Button
                onClick={apply}
                disabled={state === "applying"}
                className="gap-1.5 bg-teal text-white hover:bg-teal/80"
              >
                {state === "applying" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wrench className="w-4 h-4" />}
                Apply rebuild
              </Button>
              <span className="text-xs text-txt-muted">
                Confirm token: <span className="font-mono">{proposal.confirm_token?.slice(0, 12)}…</span>
              </span>
            </div>
          )}

          {state === "applied" && (
            <p className="text-sm text-teal flex items-center gap-1 pt-2 border-t border-navy-border">
              <CheckCircle className="w-4 h-4" /> Applied. Audit id: <span className="font-mono">{proposal.audit_id}</span>
            </p>
          )}
        </div>
      )}

      {recentRuns && (
        <div className="rounded-md border border-navy-border bg-navy-card/30 p-3 text-xs space-y-1">
          <p className="text-txt-muted uppercase">Recent recovery runs</p>
          {recentRuns.length === 0 ? (
            <p className="text-txt-muted">No runs yet.</p>
          ) : (
            <ul className="space-y-1">
              {recentRuns.map((r) => (
                <li key={r.id} className="font-mono">
                  <span className="text-txt">{r.matter_reference}</span>
                  {" · "}
                  <span className="text-txt-muted">{r.applied_at}</span>
                  {" · "}
                  <span className="text-teal">{JSON.stringify(r.applied_fields)}</span>
                  {" · by "}
                  <span className="text-txt-muted">{r.actor}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

export default function AdminTools() {
  const navigate = useNavigate();
  const [submissionId, setSubmissionId] = useState("");

  // Brief 35 — page-level director guard. This route was previously
  // unguarded (reachable by direct URL by any authenticated user).
  //
  // The guard only bounces on a *clean* negative (the role endpoint
  // answered and said is_director === false). A transient fetch failure
  // (network blip, cold backend, race on first paint) must NOT eject a
  // genuine director with a misleading "Director access only" toast —
  // that was the live bug. On fetch error we retry once, then fail open
  // for this client-side guard; the backend admin routes remain the
  // authoritative gate and reject non-directors server-side regardless.
  useEffect(() => {
    const currentUserEmail =
      sessionStorage.getItem("bonsai_user_id") ||
      localStorage.getItem("bonsai_user_id") ||
      "";
    let cancelled = false;
    const fetchRole = async () => {
      const res = await axios.get(`${API}/user-role`, {
        headers: currentUserEmail ? { "X-Bonsai-User-Id": currentUserEmail } : {},
        withCredentials: true,
      });
      return res.data;
    };
    (async () => {
      let data = null;
      try {
        data = await fetchRole();
      } catch {
        try {
          data = await fetchRole();
        } catch {
          // Could not determine role after a retry — fail open on the
          // client guard rather than ejecting a possible director.
          return;
        }
      }
      if (cancelled) return;
      if (data && data.is_director === false) {
        toast.error("Director access only");
        navigate("/");
      }
    })();
    return () => { cancelled = true; };
  }, [navigate]);

  // These routers are sealed with require_session, so the request must carry
  // the session cookie (authFetch). A plain fetch was turned away with a 401,
  // and with no response attached the error read "Couldn't reach the server".
  const call = async (method, path) => {
    const resp = await authFetch(`${BASE}${path}`, { method, headers: getHeaders() });
    if (!resp.ok) {
      const err = new Error(`${resp.status} ${resp.statusText}`);
      err.response = { status: resp.status, data: await resp.json().catch(() => null) };
      throw err;
    }
    return resp.json().catch(() => "OK");
  };
  const post = (path) => call("POST", path);
  const get = (path) => call("GET", path);

  return (
    <div className="min-h-screen bg-navy">
      {/* Top bar */}
      <nav className="glass-nav sticky top-0 z-50 w-full">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-14">
            <div className="flex items-center gap-3">
              <img src={LOGO_URL} alt="Bonsai Law" className="w-9 h-9" />
              <div>
                <h1 className="text-base font-bold text-teal leading-tight">Admin Tools</h1>
                <p className="text-[10px] text-txt-muted tracking-wide font-medium leading-tight">One-click operations</p>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate("/dashboard")}
              className="text-txt-muted border-navy-border/40 hover:border-teal/50 hover:text-teal gap-1.5"
            >
              <ArrowLeft className="w-4 h-4" />
              Dashboard
            </Button>
          </div>
        </div>
      </nav>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
        {/* Email Delivery Diagnostic — surfaces /api/admin/email-delivery-diagnostic */}
        <EmailDiagnosticTile apiBase={BASE} getHeaders={getHeaders} />

        {/* OB Submit Rejects triage — surfaces /api/admin/audit/ob-submit-rejects (PR P) */}
        <ObRejectsTriageTile apiBase={BASE} getHeaders={getHeaders} />

        <BackfillStatusTile apiBase={BASE} getHeaders={getHeaders} />

        {/* Data Sync */}
        <section className="glass-card p-6 rounded-md">
          <h2 className="text-sm font-semibold text-teal uppercase tracking-wider mb-4 flex items-center gap-2">
            <Database className="w-4 h-4" /> Data Sync
          </h2>
          <div className="space-y-3">
            {/* BM-H627 (26 Sep 2026): the WIP sync is May's alone; its button
                is on her Accounts panel on the home page. */}
            {/* BM-H265: this used to be the ONLY way the conflict index
                ever reached Contacts, which is why 4,298 of 4,878 index
                titles had never arrived. It now runs hourly on the
                scheduler as well, and this button is a manual nudge
                rather than the whole mechanism. The result is surfaced
                so a skipped or suspicious run is visible instead of
                looking like a success. */}
            <ActionButton
              label="Sync Contacts from SharePoint conflict index"
              icon={Database}
              onClick={async () => {
                const resp = await post("/api/conflicts/seed-contacts-from-sharepoint");
                if (resp?.status === "skipped") {
                  return `Not run: ${resp.reason}`;
                }
                if (resp?.status === "suspicious") {
                  return `Looks wrong, not treating this as a success: ${resp.reason}`;
                }
                return (
                  `${resp?.created ?? 0} contact(s) created, ` +
                  `${resp?.skipped ?? 0} already present, ` +
                  `${resp?.linked ?? 0} matter link(s) added, ` +
                  `from ${resp?.total_items ?? 0} index row(s). ` +
                  `Also runs hourly on the scheduler.`
                );
              }}
            />
            <ActionButton
              label="Backfill CaseMinder clients → SharePoint"
              icon={Database}
              onClick={() => post("/api/conflicts/backfill-to-sharepoint")}
            />
            <ActionButton
              label="Backfill invoice request fields"
              icon={RefreshCw}
              onClick={() => get("/api/admin/backfill-invoice-requests")}
            />
            <ActionButton
              label="File-note PDFs — dry-run"
              icon={RefreshCw}
              onClick={async () => {
                const resp = await get("/api/admin/backfill-file-note-pdfs");
                return `Would file ${resp.planned} PDF(s) across ${resp.matters_touched} matter(s). Skipped (no matter): ${resp.skipped_no_matter}. Run ‘File-note PDFs — apply’ to execute.`;
              }}
            />
            <ActionButton
              label="File-note PDFs — apply"
              icon={RefreshCw}
              onClick={async () => {
                const resp = await post("/api/admin/backfill-file-note-pdfs?apply=true");
                const failN = resp.failed || 0;
                const failSummary = failN > 0 && resp.failures?.length
                  ? ` First failure: ${resp.failures[0].matter_ref} — ${resp.failures[0].reason}.`
                  : "";
                return `Filed ${resp.filed} PDF(s), failed ${failN}, across ${resp.matters_touched} matter(s). Skipped (no matter): ${resp.skipped_no_matter}.${failSummary}`;
              }}
            />
            <ActionButton
              label="Backfill Filed Categories"
              icon={RefreshCw}
              onClick={async () => {
                const resp = await post("/api/bonsai/filing/backfill-categories");
                return `Applied: ${resp.applied}, Skipped: ${resp.skipped}, Failed: ${resp.failed}, Remaining: ${resp.remaining}${resp.run_again ? ' — run again to process more' : ''}`;
              }}
            />
            <ActionButton
              label="Backfill Internet Message IDs"
              icon={RefreshCw}
              onClick={async () => {
                const resp = await post("/api/bonsai/filing/backfill-internet-message-ids");
                return `Updated: ${resp.updated}, Not found: ${resp.not_found}, Failed: ${resp.failed}, Remaining: ${resp.remaining}${resp.remaining > 0 ? ' — run again to process more' : ''}`;
              }}
            />

            {/* Reference Fixer — lookup first, then per-matter */}
            <ReferenceFixer />
          </div>
        </section>

        {/* QuickBooks */}
        <section className="glass-card p-6 rounded-md">
          <h2 className="text-sm font-semibold text-teal uppercase tracking-wider mb-4 flex items-center gap-2">
            <Link2 className="w-4 h-4" /> QuickBooks
          </h2>
          <div className="space-y-3">
            <ActionButton
              label="Connect QuickBooks"
              icon={Link2}
              onClick={async () => {
                const resp = await get("/api/credit-control/qbo/authorize");
                if (resp?.auth_url) {
                  window.open(resp.auth_url, "_blank");
                  return "Opened QBO auth window";
                }
                return resp;
              }}
            />
            <ActionButton
              label="Check QBO status"
              icon={Activity}
              onClick={() => get("/api/credit-control/qbo/status")}
            />
            <ActionButton
              label="Manual QBO sync"
              icon={RefreshCw}
              onClick={() => post("/api/credit-control/qbo/sync")}
            />
            <ActionButton
              label="Auto-create CC records from QBO"
              icon={FolderPlus}
              onClick={() => post("/api/credit-control/qbo/auto-create-records")}
            />
            {/* BM-H4437: CaseMinder invoices to QuickBooks */}
            <p className="text-xs font-semibold text-txt-muted uppercase tracking-wider pt-2">Invoice sync</p>
            <QboInvoiceSyncTile />
          </div>
        </section>

        {/* Rebuild Matter — disaster recovery */}
        <section className="glass-card p-6 rounded-md">
          <h2 className="text-sm font-semibold text-teal uppercase tracking-wider mb-4 flex items-center gap-2">
            <Wrench className="w-4 h-4" /> Rebuild Matter
          </h2>
          <p className="text-xs text-txt-muted mb-3">
            Reconstruct a matter's pipeline state from the primary documents
            already saved in SharePoint (conflict check, OB, FORF, CCL).
            Always preview as a dry-run first; the apply button only appears
            when there are changes and SharePoint can confirm the docs.
          </p>
          <RebuildMatter />
        </section>

        {/* Residential Folders */}
        <section className="glass-card p-6 rounded-md">
          <h2 className="text-sm font-semibold text-teal uppercase tracking-wider mb-4 flex items-center gap-2">
            <FolderPlus className="w-4 h-4" /> Residential Folders
          </h2>
          <div className="flex items-start gap-3">
            <Input
              placeholder="Submission ID"
              value={submissionId}
              onChange={(e) => setSubmissionId(e.target.value)}
              className="w-48 bg-navy-card border-navy-border text-txt placeholder:text-txt-muted"
            />
            <ActionButton
              label="Create residential folders"
              icon={FolderPlus}
              onClick={() => {
                if (!submissionId.trim()) throw new Error("Enter a submission ID");
                return post(`/api/admin/create-residential-folders/${submissionId.trim()}`);
              }}
            />
          </div>
        </section>

        {/* Relink Invoice Requests — Brief 48. MD utility for re-attaching
            invoice requests to the correct matter. Lives at
            /finance/relink-invoice-requests; this is its entry point. */}
        <section className="glass-card p-6 rounded-md">
          <h2 className="text-sm font-semibold text-teal uppercase tracking-wider mb-4 flex items-center gap-2">
            <Link2 className="w-4 h-4" /> Relink Invoice Requests
          </h2>
          <p className="text-xs text-txt-muted mb-3">
            Review invoice requests that could not be matched to a matter and
            re-attach them to the correct one.
          </p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate("/finance/relink-invoice-requests")}
            data-testid="admin-tools-relink-invoice-requests"
            className="border-navy-border text-txt hover:bg-navy-hover"
          >
            Open relink queue
          </Button>
        </section>

        <p className="text-xs text-txt-muted text-center">
          Logged in as {getUserEmail()}
        </p>
      </main>
    </div>
  );
}
